import { useEffect, useRef, useState, useCallback, type FC } from 'react';
import {
  getDefaultReactSlashMenuItems,
  SuggestionMenuController,
  useCreateBlockNote,
} from '@blocknote/react';
import { BlockNoteView } from '@blocknote/shadcn';
import '@blocknote/shadcn/style.css';
import { cn } from '@/client/lib/utils';
import { Button } from '@/client/components/ui/button';
import { Eye, EyeOff } from 'lucide-react';

interface MarkdownEditorProps {
  content: string;
  isStreaming: boolean;
  onContentChange: (markdown: string) => void;
}

export const MarkdownEditor: FC<MarkdownEditorProps> = ({
  content,
  isStreaming,
  onContentChange,
}) => {
  const editor = useCreateBlockNote({});
  const [isRawView, setIsRawView] = useState(false);
  const [rawMarkdown, setRawMarkdown] = useState('');

  // Guard against feedback loops: skip onChange when we're programmatically updating blocks
  const isProgrammaticUpdate = useRef(false);
  // Track the last content we synced from props to avoid redundant updates
  const lastSyncedContent = useRef('');

  // Sync external content changes → editor blocks (streaming updates, file switches)
  useEffect(() => {
    if (content === lastSyncedContent.current) return;
    lastSyncedContent.current = content;

    isProgrammaticUpdate.current = true;
    try {
      const blocks = editor.tryParseMarkdownToBlocks(content);
      editor.replaceBlocks(editor.document, blocks);
    } finally {
      // Small delay to let onChange fire and be ignored before clearing the flag
      requestAnimationFrame(() => {
        isProgrammaticUpdate.current = false;
      });
    }
  }, [content, editor]);

  // User edits in editor → convert to markdown → call onContentChange
  const onChange = useCallback(() => {
    if (isStreaming || isProgrammaticUpdate.current) return;

    const markdown = editor.blocksToMarkdownLossy(editor.document);
    const cleaned = markdown.replaceAll('\\\n', '\n');
    lastSyncedContent.current = cleaned;
    onContentChange(cleaned);
  }, [isStreaming, editor, onContentChange]);

  // Toggle to raw view: extract current markdown from editor
  useEffect(() => {
    if (isRawView) {
      const md = editor.blocksToMarkdownLossy(editor.document);
      setRawMarkdown(md.replaceAll('\\\n', '\n'));
    }
  }, [isRawView, editor]);

  // Toggle back from raw view: parse raw markdown back into editor
  const handleExitRawView = useCallback(() => {
    isProgrammaticUpdate.current = true;
    try {
      const blocks = editor.tryParseMarkdownToBlocks(rawMarkdown);
      editor.replaceBlocks(editor.document, blocks);
      lastSyncedContent.current = rawMarkdown;
      onContentChange(rawMarkdown);
    } finally {
      requestAnimationFrame(() => {
        isProgrammaticUpdate.current = false;
      });
    }
    setIsRawView(false);
  }, [rawMarkdown, editor, onContentChange]);

  return (
    <div className="flex h-full flex-col">
      {/* Raw/Rendered toggle */}
      <div className="flex justify-end px-2 py-1">
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          onClick={() => (isRawView ? handleExitRawView() : setIsRawView(true))}
          title={isRawView ? 'View rendered' : 'View raw markdown'}
        >
          {isRawView ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {isRawView ? (
          <textarea
            className="h-full w-full resize-none bg-background p-3 font-mono text-xs leading-relaxed outline-none"
            value={rawMarkdown}
            onChange={(e) => setRawMarkdown(e.target.value)}
            spellCheck={false}
          />
        ) : (
          <BlockNoteView
            theme="light"
            formattingToolbar={false}
            slashMenu={false}
            sideMenu={false}
            tableHandles={false}
            onChange={onChange}
            editable={!isStreaming}
            editor={editor}
            className={cn(isStreaming ? 'pulse-text' : '', 'gaia-blocknote-theme')}
          >
            <SuggestionMenuController
              getItems={async () =>
                getDefaultReactSlashMenuItems(editor).filter((item) => item.group !== 'Media')
              }
              triggerCharacter="/"
            />
          </BlockNoteView>
        )}
      </div>
    </div>
  );
};
